import React, { useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { logger } from 'logging';
import debounce from 'lodash-es/debounce';
import { useSelector } from 'react-redux';
import styled from 'App/styled';
import { useStringFromId } from '@nykaa/nes-intl-shared';
import SearchIcon from 'components/Icons/Search';
import { localStorageService } from 'service/storage';
import Cross from 'components/Icons/CrossVoice';
import VoiceSearch from 'components/VoiceSearch';
import { handleManualSearch } from 'helpers/utils/search/searchSuggestion';
import {
  LAST_VISITED_SEARCH_PAGE,
  LAST_VISITED_PAGE,
} from 'constants/searchSuggestion';
import { State } from 'store/types';
import { getPlaceholderText } from 'components/Header/Search/helper';
import { getUpdatedPageType } from 'shared/helpers';

const elemName = 'search-suggestions-nykaa';
const Form = styled.form`
  display: flex;
  flex: 1;
  align-items: center;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 5px 30px 5px 5px;
  margin: 4px 15px 4px 0;
  ${({ theme }) => theme.typography.bodyMedium};
  border-radius: 4px;
  border: 1px solid #c3c3c3;
  &:focus {
    outline: none;
  }
`;

const IconWrapper = styled.button`
  border: none;
  background: transparent;
  position: absolute;
  inset-inline-end: 12px;
  padding: 4px;
  border: none;
  background: transparent;
  margin: 0 8px;
  display: flex;
  align-items: center;
  svg {
    opacity: 48%;
  }

  [dir='rtl'] & svg {
    transform: scaleX(-1)
  }
`;

interface SearchBarProps {
  pageType: string;
  maxSearchHistoryCount: number;
  fetchSearchSuggestion: (val: any, isPro: boolean) => Promise<void>;
  updateSearchField: (val: any) => Promise<void>;
  isPro: boolean;
  hasVoiceSearch: boolean;
  showVoiceModal: boolean;
  toggleVoiceModal: () => void;
}

const SearchBar = ({
  pageType,
  maxSearchHistoryCount,
  fetchSearchSuggestion,
  updateSearchField,
  isPro,
  showVoiceModal,
  hasVoiceSearch,
  toggleVoiceModal,
}: SearchBarProps) => {
  const hasSetPlaceholder = useRef(false);
  let placeholder = useStringFromId(getPlaceholderText());
  const inputRef = useRef(null);
  const intialText = useRef('');
  const search = useLocation().search;
  const urlParams = new URLSearchParams(search); // ? Get last page (lp) Query Param from the url
  const updatedPageType = getUpdatedPageType(search, pageType);
  const prevRoute = urlParams.get(LAST_VISITED_PAGE);

  const CHECK_PREV_ROUTE = 'searchResults';
  const searchField = useSelector(
    (state: State) => state.searchSuggestions.searchField
  );

  const updateSearchResultsAndField = useCallback(
    (val: string = '') => {
      updateSearchField(val);
      fetchSearchSuggestion(val, isPro);
    },
    [fetchSearchSuggestion, isPro, updateSearchField]
  );

  const onChange = debounce((val) => {
    updateSearchResultsAndField(val);
  }, 100);

  useEffect(() => {
    if (inputRef?.current) {
      if (!showVoiceModal) {
        inputRef?.current?.focus();
      } // ? This gives linting error for some weird reason - need to look up
      const lastVisitedSearchPage = localStorageService.getItem(
        LAST_VISITED_SEARCH_PAGE
      ); // ? Get Latest Visited Search Page
      if (
        lastVisitedSearchPage &&
        prevRoute === CHECK_PREV_ROUTE &&
        !hasSetPlaceholder?.current &&
        !intialText.current
      ) {
        try {
          inputRef.current.value = lastVisitedSearchPage;
          intialText.current = lastVisitedSearchPage;
          hasSetPlaceholder.current = true;
          onChange(lastVisitedSearchPage);
        } catch (err) {
          logger.error(err, 'Parsing "search history"');
        }
      }
    }
  }, [onChange, prevRoute, showVoiceModal, updateSearchResultsAndField]);

  useEffect(() => {
    if (searchField !== inputRef.current.value) {
      updateSearchResultsAndField(searchField);
      inputRef.current.value = searchField;
    }
  }, [searchField, updateSearchResultsAndField]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const suggestionElement = e.target.elements[elemName];
    if (suggestionElement) {
      const searchTerm = suggestionElement.value;
      if (searchTerm) {
        handleManualSearch(
          { page: updatedPageType, searchQuery: searchTerm },
          maxSearchHistoryCount
        );
      }
    }
  };

  const handleCross = () => {
    if (searchField) {
      updateSearchField('');
      inputRef.current.value = '';
    }
  };

  const handleVoiceSearch = useCallback(
    (text: string) => {
      inputRef.current.value = text;
      updateSearchField(text);
      handleManualSearch(
        { page: updatedPageType, searchQuery: text },
        maxSearchHistoryCount,
        true
      );
    },
    [maxSearchHistoryCount, updatedPageType, updateSearchField]
  );
  const enableVoiceSearch = hasVoiceSearch && (!searchField || showVoiceModal);
  const iconClass = searchField ? 'cross-icon' : '';
  const showVoiceIcon = Boolean(!searchField);
  return (
    <>
      <Form onSubmit={handleSubmit}>
        <StyledInput
          placeholder={placeholder}
          name={elemName}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          ref={inputRef}
        />
        {!enableVoiceSearch && !searchField && (
          <IconWrapper
            className={iconClass}
            type="submit"
          >
            <SearchIcon />
          </IconWrapper>
        )}
      </Form>
      {searchField && (
        <IconWrapper
          className={iconClass}
          onClick={handleCross}
        >
          <Cross />
        </IconWrapper>
      )}
      {enableVoiceSearch && (
        <VoiceSearch
          showVoiceIcon={showVoiceIcon}
          showVoiceModal={showVoiceModal}
          handleVoiceSearch={handleVoiceSearch}
          toggleVoiceModal={toggleVoiceModal}
        />
      )}
    </>
  );
};

export default SearchBar;
